import React, { useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ACCOUNTS } from "../../../constants";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { formatLocalDateOnly } from "../../shared utils/dateFormatters";
import { enGB } from "date-fns/locale";

const DISCOUNT_PREFIX = "DISCOUNT:";

function parseDiscountReason(paymentaccount) {
  const s = (paymentaccount || "").trim();
  if (!s.toUpperCase().startsWith(DISCOUNT_PREFIX)) return "";
  return s.slice(DISCOUNT_PREFIX.length).trim();
}

function buildDiscountAccount(reason) {
  const r = (reason || "").trim();
  return r ? `${DISCOUNT_PREFIX} ${r}` : `${DISCOUNT_PREFIX} (no reason)`;
}

function PaymentDialog({
  studentId,
  addPayment,
  initialData = null,
  trigger,
  open: controlledOpen,
  onClose: controlledOnClose,
}) {
  const [internalOpen, setInternalOpen] = useState(false);

  const isControlled = typeof controlledOpen === "boolean";
  const open = isControlled ? controlledOpen : internalOpen;

  const initialIsDiscount = useMemo(() => {
    const acc = initialData?.paymentaccount || "";
    return acc.toUpperCase().startsWith(DISCOUNT_PREFIX);
  }, [initialData]);

  const [isDiscount, setIsDiscount] = useState(initialIsDiscount);
  
  const [discountReason, setDiscountReason] = useState(
    parseDiscountReason(initialData?.paymentaccount)
  );

  const [payment, setPayment] = useState(
    normalisePaymentData(initialData, studentId)
  );

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const acc = initialData?.paymentaccount || "";
    const disc = acc.toUpperCase().startsWith(DISCOUNT_PREFIX);

    setIsDiscount(disc);
    setDiscountReason(parseDiscountReason(acc));
    setPayment(normalisePaymentData(initialData, studentId));
  }, [initialData, studentId]);

  const handleClickOpen = () => {
    if (!isControlled) {
      setInternalOpen(true);
    }
  };

  const handleClose = () => {
    if (isControlled) {
      controlledOnClose?.();
    } else {
      setInternalOpen(false);
    }
  };

  const handleDateChange = (date, name) => {
    setPayment((prev) => ({
      ...prev,
      [name]: date,
    }));
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setPayment((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    // Guard against a racy double-click firing a second POST while the first
    // is still in flight.
    if (saving) return;
    setSaving(true);

    const paymentaccount = isDiscount
      ? buildDiscountAccount(discountReason)
      : payment.paymentaccount;

    const cleanPayment = {
      ...payment,
      studentId,
      paymentaccount,
      paymentamount:
        payment.paymentamount === "" || payment.paymentamount === null
          ? ""
          : Math.abs(Number(payment.paymentamount)),
      paymentdate: formatLocalDateOnly(payment.paymentdate),
    };

    try {
      // addPayment resolves truthy ("keep open") when the user declined a
      // duplicate, so they can adjust the entry rather than lose it.
      const keepOpen = await Promise.resolve(addPayment(cleanPayment));
      if (!keepOpen) {
        handleClose();
      }
    } finally {
      setSaving(false);
    }
  };

  function normalisePaymentData(data, studentId) {
    if (!data) {
      return {
        studentId,
        paymentamount: "",
        paymentaccount: "",
        paymentdate: new Date(),
      };
    }

    let parsedDate = null;

    if (data.paymentdate instanceof Date) {
      parsedDate = data.paymentdate;
    } else if (typeof data.paymentdate === "string" && data.paymentdate) {
      // safest for yyyy-mm-dd values
      const parts = data.paymentdate.split("-");
      if (parts.length === 3) {
        const [year, month, day] = parts.map(Number);
        parsedDate = new Date(year, month - 1, day);
      } else {
        const fallback = new Date(data.paymentdate);
        parsedDate = Number.isNaN(fallback.getTime()) ? null : fallback;
      }
    }

    if (!parsedDate || Number.isNaN(parsedDate.getTime())) {
      parsedDate = new Date();
    }

    return {
      ...data,
      studentId,
      paymentdate: parsedDate,
    };
  }

  return (
    <div>
      {trigger &&
        React.cloneElement(trigger, {
          onClick: handleClickOpen,
        })}

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>
          {isDiscount ? "Record A Discount" : "Record A Payment"}
        </DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
            <Stack spacing={2} mt={1}>
              <TextField
                label="Student DB ref ID"
                name="studentId"
                autoFocus
                variant="standard"
                value={payment.studentId}
                InputProps={{ readOnly: true }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={isDiscount}
                    onChange={(e) => setIsDiscount(e.target.checked)}
                  />
                }
                label="This is a discount"
              />

              <DatePicker
                label={isDiscount ? "Discount Date" : "Payment Date"}
                value={payment.paymentdate || null}
                onChange={(date) => handleDateChange(date, "paymentdate")}
                renderInput={(params) => (
                  <TextField {...params} fullWidth sx={{ mb: 0, mt: 0 }} />
                )}
              />

              <TextField
                label={isDiscount ? "Discount Amount (£)" : "Payment Amount (£)"}
                name="paymentamount"
                type="number"
                variant="standard"
                value={payment.paymentamount}
                onChange={handleChange}
              />

              {isDiscount ? (
                <TextField
                  label="Discount reason"
                  variant="standard"
                  value={discountReason}
                  onChange={(e) => setDiscountReason(e.target.value)}
                  placeholder="e.g. Early bird / Agent goodwill / Sibling"
                />
              ) : (
                <FormControl variant="standard">
                  <InputLabel id="account-label">Account</InputLabel>
                  <Select
                    labelId="account-label"
                    id="paymentaccount"
                    name="paymentaccount"
                    value={payment.paymentaccount}
                    onChange={handleChange}
                    label="Account"
                  >
                    {ACCOUNTS.map((account) => (
                      <MenuItem key={account.value} value={account.value}>
                        {account.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Stack>
          </LocalizationProvider>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default PaymentDialog;